import { Button } from "@mui/material";

import { AmbulanzResponse } from "@/api";
import { useAmbulanz } from "@/common/components/ambulanzes/ambulanz-widget/hooks/useAmbulanz";
import { AmbulanzFormFields } from "@/common/components/ambulanzes/shared/AmbulanzFormFields";
import { FormDialog } from "@/common/components/form-dialog/FormDialog";
import { useTranslation } from "@/translations";

import { useAmbulanzEdit } from "./hooks/useAmbulanzEdit";

interface Props {
  ambulanz: AmbulanzResponse;
  onClose: () => void;
}

export function AmbulanzEditDialog({ ambulanz, onClose }: Props) {
  const { t } = useTranslation();
  const { formik, typeOptions } = useAmbulanzEdit(ambulanz, onClose);
  const { isOpen, isClaimed, isOwn, claim, returnAmbulanz, handleComplete, isBusy } = useAmbulanz(ambulanz, {
    onActionSuccess: onClose,
  });

  const ownClaimed = isOwn && isClaimed;
  const canEdit = isOpen || ownClaimed;

  return (
    <FormDialog
      title={t("ambulanzes.editDialog.title")}
      onClose={onClose}
      formik={formik}
      submitLabel={canEdit ? t("common.save") : undefined}
      fullWidth
      extraAction={{
        left: ownClaimed ? (
          <Button
            variant="outlined"
            color="primary"
            onClick={() => returnAmbulanz()}
            disabled={isBusy || formik.isSubmitting}
          >
            {t("ambulanzes.actions.return")}
          </Button>
        ) : undefined,
        right: isOpen ? (
          <Button variant="outlined" color="primary" onClick={() => claim()} disabled={isBusy || formik.isSubmitting}>
            {t("ambulanzes.actions.claim")}
          </Button>
        ) : ownClaimed ? (
          <Button variant="outlined" color="primary" onClick={handleComplete} disabled={isBusy || formik.isSubmitting}>
            {t("ambulanzes.actions.complete")}
          </Button>
        ) : undefined,
      }}
    >
      <AmbulanzFormFields
        formik={formik}
        typeOptions={typeOptions}
        clientReadOnly
        clientId={ambulanz.clientId}
        clientName={ambulanz.clientFullName ?? ambulanz.clientName}
        detailsReadOnly={!isOpen}
        noteReadOnly={!canEdit}
      />
    </FormDialog>
  );
}
